<template>

    <v-input-group>
        <div class="datepicker" :class="'size-' + size">
            <datepicker
                :disabled="disabled"
                :format="format"
                :language="language"
                :placeholder="placeholder ?? $t('labels.select_date')"
                :typeable="false"
                :value="val"
                calendar-class="datepicker-calendar"
                input-class="datepicker-input"
                maximum-view="day"
                @selected="onSelected">
            </datepicker>
            <div class="arrow">
                <i class="fas fa-chevron-down ml-5"></i>
            </div>
        </div>
        <template v-if="clearable && val" #suffix>
            <a href="#" @click.prevent="clear">Clear</a>
        </template>
    </v-input-group>

</template>

<style lang="scss">

    .datepicker {
        position: relative;
        width: 100%;
        &.size-sm {
            .datepicker-input {
                height: 40px;
                padding: 0 12px;
            }
            .datepicker-calendar {
                margin-bottom: 45px;
            }
            .arrow {
                height: 40px;
                width: 40px;
            }
        }
        &.size-md {
            .datepicker-input {
                height: 50px;
                padding: 0 15px;
            }
            .datepicker-calendar {
                margin-bottom: 55px;
            }
            .arrow {
                height: 50px;
                width: 50px;
            }
        }
        .arrow {
            color: $color-text;
            display: grid;
            font-size: 11px;
            height: 50px;
            position: absolute;
            pointer-events: none;
            place-items: center;
            right: 0;
            top: 0;
        }
    }

    .datepicker-input {
        @include font-medium(13px);
        appearance: none;
        background: $color-white;
        border: 1px solid $color-rule;
        border-radius: 5px;
        box-sizing: border-box;
        color: $color-text;
        cursor: pointer;
        outline: none;
        transition: border-color .3s;
        width: 100%;
        &:hover, &:focus {
            border-color: $color-text;
            color: $color-text;
        }
        &:disabled {
            background: $color-bg;
            border-color: $color-rule;
            color: $color-text;
            opacity: 1;
        }
    }

    .datepicker-calendar {
        @include font-medium(13px);
        @include shad(2);
        background: $color-white !important;
        border: 1px solid $color-rule !important;
        border-radius: 5px;
        margin-top: 5px;
        max-width: 250px;
        overflow: hidden;
        padding: 10px;
        header span, .cell.day, .cell.day-header {
            border: none;
            color: $color-text;
            height: 30px;
            line-height: 30px;
            margin: 0;
            padding: 0;
        }
        header {
            span.prev, span.next {
                background: none !important;
                color: $color-text;
                transition: opacity .3s;
                &:hover {
                    opacity: 0.5;
                }
            }
            span.prev::after {
                border-right-color: $color-text;
            }
            span.next::after {
                border-left-color: $color-text;
            }
            .day__month_btn {
                cursor: default;
            }
        }
        .cell.day-header {
            cursor: default !important;
        }
        .cell.day {
            border-radius: 5px;
            transition: background-color .3s;
            &:hover {
                border: none !important;
                color: $color-primary;
            }
            &.today {
                background-color: $color-bg;
            }
            &.selected {
                background-color: $color-primary;
                color: $color-white;
                &:hover {
                    background-color: $color-primary;
                }
            }
        }
    }

</style>

<script>

import { de, en, es, fr, it, nl, pl, sv } from 'vuejs-datepicker/dist/locale';
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';

export default {

    components: {
        'datepicker': Datepicker,
    },

    props: {
        model: {
            required: true,
        },
        size: {
            type: String,
            default: 'md',
        },
        timezone: {
            type: String,
        },
        placeholder: {
            type: String,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        // Field display format.
        format: {
            type: String,
            default: 'MMM, dd yyyy',
        },
        // Input and output date format.
        unix: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            val: null,
            language: null,
        }
    },

    created() {
        this.val = this.getValue();
        this.setLanguage();
    },

    watch: {

        model(val) {
            this.val = this.getValue(val);
        },

        timezone(val) {
            let date = moment(this.val).tz(val).startOf('day').utc();
            if (this.unix) {
                date = date.unix();
            } else {
                date = date.format('YYYY-MM-DD');
            }
            this.$emit('input', date);
        },

    },

    methods: {

        setLanguage() {
            const lang = this.$store.getters.lang || 'en';
            const locales = { de, en, es, fr, it, nl, pl, sv };
            this.language = locales[lang] || locales.en;
        },

        onSelected(val) {
            let date = moment(val).startOf('day').utc();
            if (this.timezone) {
                date = moment(val).tz(this.timezone).startOf('day').utc();
            }
            if (this.unix) {
                date = date.unix();
            } else {
                date = date.format('YYYY-MM-DD');
            }
            this.val = val;
            this.$emit('input', date);
        },

        getValue(val = null) {
            let date = val ?? this.model;
            if (date && this.unix) {
                return moment.unix(date).toISOString();
            } else if (date) {
                return moment(date).toISOString();
            }
        },

        clear() {
            this.val = null;
            this.$emit('input', null);
        },

    },

}

</script>
